import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Clock, ExternalLink, FileDown, Languages, Link2, Mail } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api, ApiError, errorMessage, rawFetch, unwrap } from "../api/client";
import { FAILED_NOTE, proposalView, resendView, UPDATING_LABEL, UPDATING_TOOLTIP, whenText } from "../lib/resend";
import { ResendConfirm } from "./ResendConfirm";
import { EsignStatusLine, SendProposalButton } from "./SendProposal";
import { useOrg } from "../api/org";
import { Button, cx, useToast } from "./ui";
import { useT } from "../i18n/I18nProvider";
import { InternalCopyDialog } from "../i18n/HomeownerLanguage";
import { localeInfo } from "../i18n/negotiate.ts";

function filenameOf(response: Response, fallback: string): string {
  const match = /filename="([^"]+)"/.exec(response.headers.get("Content-Disposition") || "");
  return match?.[1] ?? fallback;
}

/** Copies text produced by `make` (an API call) inside the click, so Safari keeps the user gesture. */
async function copyFrom(make: () => Promise<string>): Promise<string> {
  if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
    const text = make();
    try {
      await navigator.clipboard.write([new ClipboardItem({ "text/plain": text.then((t) => new Blob([t], { type: "text/plain" })) })]);
      return text;
    } catch {
      /* fall back to writeText below */
      const value = await text;
      await navigator.clipboard.writeText(value);
      return value;
    }
  }
  const value = await make();
  await navigator.clipboard.writeText(value);
  return value;
}

/** Downloads a proposal PDF from `path` (an API path) under the server's file name; `copy` (a language) downloads
 * the installer's internal copy in that language (`?copy=internal&locale=`: marked, never sent or signed). */
function useProposalDownload(path: string) {
  const t = useT();
  const toast = useToast();
  return useMutation({
    mutationFn: async (copy?: string) => {
      const response = await rawFetch(copy ? `${path}?copy=internal&locale=${encodeURIComponent(copy)}` : path);
      const url = URL.createObjectURL(await response.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameOf(response, "proposal.pdf");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
      return a.download;
    },
    onSuccess: (name) => toast(t("homeowner.proposal.downloaded", { name })),
    onError: (e) => toast(errorMessage(e), true),
  });
}

/** "Download proposal" for a design without a website quote (the Designer's): its price, no homeowner, in the
 * company's language for customers; and a copy in any of the 50 for the installer. */
export function DesignProposalDownload({ designId }: { designId: string }) {
  const t = useT();
  const download = useProposalDownload(`/api/v1/designs/${designId}/proposal.pdf`);
  const [copying, setCopying] = useState(false);
  return (
    <>
      <Button icon={<FileDown />} loading={download.isPending && !copying} onClick={() => download.mutate(undefined)}>
        {t("homeowner.proposal.download")}
      </Button>
      <Button icon={<Languages />} onClick={() => setCopying(true)} data-testid="proposal-copy">
        {t("homeowner.proposal.copy")}
      </Button>
      {copying && (
        <InternalCopyDialog kind="proposal" busy={download.isPending} onClose={() => setCopying(false)}
          onConfirm={(code) => download.mutate(code, { onSettled: () => setCopying(false) })} />
      )}
    </>
  );
}

/** The proposal panel's actions: Share (copy the homeowner's link; it keeps working, nothing is emailed),
 * Resend (email the quote again, after a confirmation; limited and idempotent on the server), Download proposal
 * (the proposal PDF) and Send proposal (e-signature with Bona Fide Sign; components/SendProposal.tsx). After a design save the lead is priced again on the new version (`proposal.status`
 * `updating`, a few seconds): Resend and Download wait for it, then the panel refreshes the quote. */
export function ProposalActions({ orgId, publicQuoteId, linkLast4, homeownerName, address, quoteNumber, locale }: {
  orgId: string;
  publicQuoteId: string;
  linkLast4?: string | null;
  homeownerName?: string | null;
  address?: string | null;
  quoteNumber?: string | null;
  /** The customer's language: the proposal and the quote page are in it (the internal copies are not). */
  locale?: string | null;
}) {
  const t = useT();
  const canEdit = useOrg().can("designs_leads", "edit");
  const [copy, setCopy] = useState<"proposal" | "page" | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const path = { org_id: orgId, quote_id: publicQuoteId };
  const key = ["proposal-resend", orgId, publicQuoteId];
  const [shareFallback, setShareFallback] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const idempotency = useRef<string | null>(null);

  const resendState = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/public-quotes/{quote_id}/resend", { params: { path } })),
    // Faster while the proposal is updating; also while the confirmation is open (a save elsewhere holds it back).
    refetchInterval: (q) =>
      q.state.data?.proposal?.status === "updating" ? 2000 : q.state.data?.last_status === "scheduled" || confirming ? 3000 : false,
  });

  // The proposal finished updating: the offer, the price and the leads list show the new version.
  const proposalStatus = resendState.data?.proposal?.status ?? null;
  const previousStatus = useRef(proposalStatus);
  useEffect(() => {
    if (previousStatus.current === "updating" && proposalStatus && proposalStatus !== "updating") {
      qc.invalidateQueries({ queryKey: ["lead-offer", orgId, publicQuoteId] });
      qc.invalidateQueries({ queryKey: ["leads-infinite"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
      if (proposalStatus === "failed") toast(t(FAILED_NOTE), true);
    }
    previousStatus.current = proposalStatus;
  }, [proposalStatus, orgId, publicQuoteId, qc, toast, t]);

  const share = useMutation({
    mutationFn: () =>
      copyFrom(async () => (await unwrap(api.POST("/v1/orgs/{org_id}/public-quotes/{quote_id}/share", { params: { path } }))).link_url),
    onSuccess: () => {
      setShareFallback(null);
      toast(t("designs.proposal.linkCopied"));
      qc.invalidateQueries({ queryKey: ["lead-offer", orgId, publicQuoteId] });
    },
    onError: async (e) => {
      if (e instanceof ApiError) return toast(errorMessage(e), true);
      // The clipboard refused: show the link to copy by hand (the call itself worked).
      const link = await unwrap(api.POST("/v1/orgs/{org_id}/public-quotes/{quote_id}/share", { params: { path } })).then((r) => r.link_url, () => null);
      setShareFallback(link);
      toast(t("designs.proposal.copyFallback"), true);
    },
  });

  const resend = useMutation({
    mutationFn: () => {
      idempotency.current ??= crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
      return unwrap(api.POST("/v1/orgs/{org_id}/public-quotes/{quote_id}/resend", { params: { path, header: { "Idempotency-Key": idempotency.current } } }));
    },
    onSuccess: (state) => {
      idempotency.current = null;
      setConfirming(false);
      qc.setQueryData(key, state);
      qc.invalidateQueries({ queryKey: ["lead-activity", orgId, publicQuoteId] });
      if (!state.replayed) toast(state.recipient ? t("designs.proposal.scheduledFor", { recipient: state.recipient }) : t("designs.proposal.scheduledForHomeowner"));
    },
    onError: (e) => {
      idempotency.current = null;
      setConfirming(false);
      const next = e instanceof ApiError ? e.problem.next_allowed_at : undefined;
      toast(next ? t("designs.resend.limitReached", { when: whenText(next, t) }) : errorMessage(e), true);
      qc.invalidateQueries({ queryKey: key });
    },
  });

  const retry = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/orgs/{org_id}/public-quotes/{quote_id}/proposal/refresh", { params: { path } })),
    onSuccess: (proposal) => {
      qc.setQueryData(key, (old: typeof resendState.data) => (old ? { ...old, proposal } : old));
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast(errorMessage(e), true),
  });

  const download = useProposalDownload(`/api/v1/orgs/${orgId}/public-quotes/${publicQuoteId}/proposal.pdf`);
  // The quote page in any language, for the installer: the homeowner's link (copying it sends nothing) opened as an
  // internal copy, marked and without actions.
  const viewPage = useMutation({
    mutationFn: async (code: string) => {
      const opened = window.open("about:blank", "_blank");
      try {
        const { link_url } = await unwrap(api.POST("/v1/orgs/{org_id}/public-quotes/{quote_id}/share", { params: { path } }));
        const url = `${link_url}?copy=internal&locale=${encodeURIComponent(code)}`;
        if (opened) opened.location.href = url;
        else window.open(url, "_blank", "noopener");
      } catch (e) {
        opened?.close();
        throw e;
      }
    },
    onSettled: () => setCopy(null),
    onError: (e) => toast(errorMessage(e), true),
  });
  const language = localeInfo(locale ?? "en").name;

  const view = resendView(resendState.data, t);
  const state = resendState.data;
  const proposal = proposalView(proposalStatus, t);
  const resendDisabled = !canEdit || !state || !state.allowed || view.busy || view.done || proposal.blocked;
  const readOnly = t("designs.proposal.readOnly");
  const resendTitle = !canEdit ? readOnly : proposal.tooltip ?? t("designs.resend.title");

  return (
    <div className="proposal-actions">
      <div className="proposal-buttons">
        <Button icon={<Link2 />} loading={share.isPending} disabled={!canEdit} onClick={() => share.mutate()}
          title={canEdit ? t("designs.proposal.shareTitle") : readOnly}>
          {t("designs.proposal.share")}
        </Button>
        <Button
          icon={view.done ? <Check /> : view.busy ? <Clock /> : <Mail />}
          loading={resend.isPending}
          disabled={resendDisabled}
          className={cx(view.done && "btn-done")}
          onClick={() => setConfirming(true)}
          title={resendTitle}
          aria-haspopup="dialog"
          aria-describedby={`resend-note-${publicQuoteId}`}
        >
          {resend.isPending ? t("designs.resend.scheduling") : view.label}
        </Button>
        <Button
          icon={<FileDown />}
          loading={download.isPending || proposal.updating}
          disabled={proposal.blocked}
          onClick={() => download.mutate(undefined)}
          title={proposal.tooltip ?? t("homeowner.proposal.downloadTitle", { language })}
          aria-describedby={`resend-note-${publicQuoteId}`}
        >
          {proposal.updating ? t(UPDATING_LABEL) : t("homeowner.proposal.download")}
        </Button>
        <SendProposalButton target={{ orgId, publicQuoteId }} disabled={proposal.blocked} title={proposal.tooltip ?? undefined} />
      </div>
      <div className="proposal-buttons proposal-languages">
        <Button size="sm" variant="ghost" icon={<Languages />} disabled={proposal.blocked} onClick={() => setCopy("proposal")}
          data-testid="proposal-copy">
          {t("homeowner.proposal.copy")}
        </Button>
        <Button size="sm" variant="ghost" icon={<ExternalLink />} disabled={!canEdit} onClick={() => setCopy("page")}
          data-testid="quote-page-copy">
          {t("homeowner.proposal.viewPage")}
        </Button>
      </div>
      {copy && (
        <InternalCopyDialog kind={copy} busy={download.isPending || viewPage.isPending} onClose={() => setCopy(null)}
          onConfirm={(code) => (copy === "proposal" ? download.mutate(code, { onSettled: () => setCopy(null) }) : viewPage.mutate(code))} />
      )}
      <div className="proposal-meta small muted" id={`resend-note-${publicQuoteId}`} aria-live="polite">
        <span>
          <Link2 size={13} style={{ verticalAlign: -2 }} /> {linkLast4 ? t("designs.proposal.linkEnding", { last4: linkLast4 }) : t("designs.proposal.noLink")}
        </span>
        {proposal.updating ? (
          <span className="proposal-status">{t(UPDATING_TOOLTIP)}</span>
        ) : proposal.failed ? (
          <span className="proposal-status proposal-failed">
            <AlertTriangle size={13} style={{ verticalAlign: -2 }} /> {t(FAILED_NOTE)} ·{" "}
            {canEdit ? (
              <button type="button" className="link-btn" disabled={retry.isPending} onClick={() => retry.mutate()}>
                {retry.isPending ? t("designs.proposal.retrying") : t("designs.proposal.retry")}
              </button>
            ) : (
              t("designs.proposal.askEditor")
            )}
          </span>
        ) : !canEdit ? (
          <span>{t("designs.proposal.viewOnly")}</span>
        ) : (
          view.note && <span>{view.note}</span>
        )}
      </div>
      <EsignStatusLine target={{ orgId, publicQuoteId }} />
      {shareFallback && (
        <input className="input proposal-link" readOnly value={shareFallback} onFocus={(e) => e.currentTarget.select()} aria-label={t("designs.proposal.homeownerLink")} dir="ltr" />
      )}
      {confirming && (
        <ResendConfirm
          name={homeownerName}
          email={state?.recipient}
          address={address}
          quoteNumber={quoteNumber}
          limits={state?.limits}
          proposalStatus={proposalStatus}
          sending={resend.isPending}
          onConfirm={() => resend.mutate()}
          onClose={() => !resend.isPending && setConfirming(false)}
        />
      )}
    </div>
  );
}
